/**
 * 认证上下文 - 全局认证状态管理
 */

import { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AuthState, AuthContextType, LoginCredentials, RegisterCredentials } from '../types/auth';
import * as authService from '../services/auth';

// 认证状态的初始值
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  loading: true, // 初始时为true，等待验证现有token
  error: null,
};

// 认证上下文
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 认证状态的Action类型
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: any; token: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_LOADING'; payload: boolean };

// 认证状态的Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    default:
      return state;
  }
}

// 认证提供者组件的Props
interface AuthProviderProps {
  children: ReactNode;
}

// 认证提供者组件
export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // 检查本地存储的token并验证
  useEffect(() => {
    async function checkExistingAuth() {
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          dispatch({ type: 'AUTH_START' });
          const isValid = await authService.validateToken();
          if (isValid) {
            const user = await authService.getProfile();
            dispatch({ 
              type: 'AUTH_SUCCESS', 
              payload: { user, token } 
            });
          } else {
            // Token无效，清除本地存储，但不触发登出状态
            localStorage.removeItem('auth_token');
            dispatch({ type: 'SET_LOADING', payload: false });
          }
        } catch (error) {
          localStorage.removeItem('auth_token');
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }

    checkExistingAuth();
  }, []);

  // 登录函数
  const login = async (credentials: LoginCredentials): Promise<{ user?: any; requiresVerification?: boolean }> => {
    const authData = await authService.login(credentials);
    
    // 检查用户是否需要邮箱验证
    if (!authData.user.emailVerified) {
      // 未验证用户：不保存token，不设置登录状态，返回用户信息用于显示引导页面
      return { user: authData.user, requiresVerification: true };
    } else {
      // 已验证用户：正常登录流程
      localStorage.setItem('auth_token', authData.token);
      dispatch({ 
        type: 'AUTH_SUCCESS', 
        payload: { user: authData.user, token: authData.token } 
      });
      return { user: authData.user, requiresVerification: false };
    }
  };

  // 注册函数
  const register = async (credentials: RegisterCredentials): Promise<{ user: any; requiresVerification: boolean }> => {
    const authData = await authService.register(credentials);
    
    // 检查用户是否需要邮箱验证
    if (!authData.user.emailVerified) {
      // 未验证用户：不保存token，不设置登录状态，返回用户信息用于显示引导页面
      return { user: authData.user, requiresVerification: true };
    } else {
      // 已验证用户：正常登录流程
      localStorage.setItem('auth_token', authData.token);
      dispatch({ 
        type: 'AUTH_SUCCESS', 
        payload: { user: authData.user, token: authData.token } 
      });
      return { user: authData.user, requiresVerification: false };
    }
  };

  // 登出函数
  const logout = (): void => {
    try {
      // 调用服务端登出（不等待结果）
      authService.logout();
    } catch (error) {
      console.warn('服务端登出失败，继续清除本地状态');
    } finally {
      // 清除本地存储和状态
      localStorage.removeItem('auth_token');
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  // 清除错误
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // 构建上下文值
  const contextValue: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// 使用认证上下文的Hook
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 